import {useEffect, useState, useCallback, memo, Fragment} from 'react';
import {api} from '../lib/auth.js';
import {kr} from '../lib/format.js';
import AccountDrill from './AccountDrill.jsx';

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'maj', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];

/**
 * Column grains. The API always ships TWELVE MONTHS and the rollup happens here, for the
 * reason rule 6 gives: how a field rolls up is a property of the field, not of the grain.
 *
 * FLOWS sum — revenue over a quarter is its three months added. STOCKS take the period's
 * LAST month, because summing three closing cash balances states the cash three times.
 * That distinction is the whole reason this cannot be a naive column-add: on this page the
 * result line and the cash line sit two rows apart and roll up by opposite rules.
 *
 * Pifa AB's financial year is 1 Jan – 31 Dec, so these are calendar quarters with no
 * fiscal offset anywhere.
 */
const GRAINS = {
	month: {label: 'Månad', spans: MONTHS.map((m, i) => ({label: m, from: i, to: i}))},
	quarter: {label: 'Kvartal', spans: [0, 1, 2, 3].map(q =>
		({label: `Q${q + 1}`, from: q * 3, to: q * 3 + 2}))},
	half: {label: 'Halvår', spans: [0, 1].map(h => ({label: `H${h + 1}`, from: h * 6, to: h * 6 + 5}))},
	year: {label: 'År', spans: [{label: 'Helår', from: 0, to: 11}]},
};

/** A flow is summed across the span; a stock takes the span's closing month. */
const roll = (values, spans, stock) => spans.map(sp => (stock
	? values[sp.to]
	: Math.round(values.slice(sp.from, sp.to + 1).reduce((a, b) => a + b, 0) * 100) / 100));

/** The period key the drill-down endpoint understands, for a given column. */
const periodKey = (year, grain, i) => (
	grain === 'month' ? `${year}-${String(i + 1).padStart(2, '0')}`
		: grain === 'quarter' ? `${year}-Q${i + 1}`
			: grain === 'half' ? `${year}-H${i + 1}` : `${year}`);

/**
 * Resultat per månad — the Fortnox report, plus the two things it does not answer.
 *
 * Fortnox stops at BERÄKNAT RESULTAT, which says whether we EARNED money. Directly beneath
 * it here is the closing cash balance, which says whether we HAVE any, and beneath that
 * every balance-sheet account month by month. On this data the difference is the whole
 * point: August books a +54 kkr result while the bank fell, because the payroll is
 * unbooked and 403 kkr left a placement account. A result line alone would have called
 * that a good month.
 *
 * Amounts default to THOUSANDS, as the Fortnox report prints them — twelve columns of full
 * kronor is a wall of digits nobody reads across. Full kronor is one click away, because
 * the moment a figure is being reconciled against something, rounding is the enemy.
 */
export default function Periodic({shadow = false}) {
	const [data, setData] = useState(null);
	const [error, setError] = useState(null);
	const [busy, setBusy] = useState(false);
	const [year, setYear] = useState(2026);
	/**
	 * Kronor by DEFAULT, thousands on request.
	 *
	 * The Fortnox report prints thousands and this page began that way, which produced
	 * exactly the misreading it invites: a cash balance of 899 179 kr shows as "899,2" and
	 * gets read as 899 kronor. This is the page people reconcile against a bank statement,
	 * and on a reconciliation page precision beats compactness — the grain switch already
	 * solves the width problem by collapsing twelve columns into four.
	 */
	const [tkr, setTkr] = useState(false);
	// Closing balance answers "where do we stand", movement answers "what happened".
	// Both are asked of a balance sheet, and neither is derivable by eye from the other.
	const [mode, setMode] = useState('balance');
	const [open, setOpen] = useState(() => new Set());
	const [grain, setGrain] = useState('month');
	// Which account is expanded, and over which period. Clicking a CELL scopes to that
	// column; clicking the account name scopes to the whole year.
	const [drill, setDrill] = useState(null);

	const load = useCallback(async () => {
		setBusy(true);
		try {
			const res = await api(`/api/monthly?year=${year}${shadow ? '&shadow=1' : ''}`);
			if (!res.ok) throw new Error(`API svarade ${res.status}`);
			setData(await res.json());
			setError(null);
		} catch (e) { setError(e.message); } finally { setBusy(false); }
	}, [year, shadow]);

	useEffect(() => { load(); }, [load]);

	const toggle = key => setOpen(s => {
		const n = new Set(s);
		n.has(key) ? n.delete(key) : n.add(key);
		return n;
	});

	if (error) return <div className="state">Kunde inte hämta rapporten: {error}</div>;
	if (!data) return <div className="state">Hämtar…</div>;

	const fmt = v => (v === 0 || v == null ? '—' : tkr ? (v / 1000).toFixed(1) : kr(v));
	// The exact kronor on every cell, whatever the display unit. A rounded figure someone is
	// checking against a bank needs a way to show its full self without changing any setting.
	const exact = v => (v == null ? undefined : `${kr(v)} kr`);
	const g = GRAINS[grain];
	const spans = g.spans;
	const flow = v => roll(v, spans, false);
	const stock = v => roll(v, spans, true);
	const openDrill = (accountNo, colIndex) => setDrill(d => {
		const period = colIndex == null ? String(data.year) : periodKey(data.year, grain, colIndex);
		return d && d.account === accountNo && d.period === period
			? null : {account: accountNo, period};
	});

	return (
		<>
			<p className="sub">
				Resultat per månad ur huvudboken — samma uppställning som Fortnox skriver ut, med
				kassan direkt under resultatet och samtliga balanskonton därunder.
			</p>

			<div className="toolbar">
				<span className="presets">
					{[2024, 2025, 2026].map(y => (
						<button key={y} className="seg" aria-pressed={year === y}
							onClick={() => setYear(y)}>{y}</button>
					))}
				</span>
				<span className="presets">
					{Object.entries(GRAINS).map(([k, v]) => (
						<button key={k} className="seg" aria-pressed={grain === k}
							onClick={() => setGrain(k)}>{v.label}</button>
					))}
				</span>
				<span className="presets">
					<button className="seg" aria-pressed={tkr} onClick={() => setTkr(true)}>tkr</button>
					<button className="seg" aria-pressed={!tkr} onClick={() => setTkr(false)}>kronor</button>
				</span>
				<span className="presets">
					<button className="seg" aria-pressed={mode === 'balance'}
						onClick={() => setMode('balance')}>Utgående saldo</button>
					<button className="seg" aria-pressed={mode === 'movement'}
						onClick={() => setMode('movement')}>Förändring</button>
				</span>
				<span className="spacer" />
				<button className="btn" onClick={load} disabled={busy}>
					{busy ? 'Hämtar…' : 'Uppdatera'}
				</button>
			</div>

			{data.shadow.active ? (
				<div className="panel panel--note">
					<div className="panel__title">Rapporten innehåller skuggbokföring</div>
					<p>
						{data.shadow.count} preliminära verifikat räknas med
						{data.shadow.months.length ? ` i ${data.shadow.months.map(m => m.month).join(', ')}` : ''}.
						Detta är inte enbart bokförda utfall.
					</p>
				</div>
			) : data.shadow.count > 0 && (
				// The signal that was missing: a booked-only report is CORRECT, and looks wrong
				// to anyone who knows what the bank actually says. Saying nothing here leaves
				// them to conclude the report is broken.
				<div className="panel panel--note">
					<div className="panel__title">
						{data.shadow.count} skuggverifikat finns men räknas INTE med
					</div>
					<p>
						Siffrorna nedan är enbart det som är bokfört i Fortnox
						{data.shadow.months.length
							? `. Skuggbokföring finns för ${data.shadow.months.map(m => m.month).join(', ')}` : ''}.
						Slå på Skuggbokföring uppe till höger för att räkna med den — särskilt Kassa
						och bank, där huvudboken ligger efter banken.
					</p>
				</div>
			)}

			<div className="unitbar">
				Belopp uttrycks i <b>{tkr ? 'tusentals kronor (tkr)' : 'kronor'}</b>
				{tkr && ' — 899,2 betyder 899 200 kr'}. Håll muspekaren över en siffra för exakt belopp.
			</div>

			<div className="table-wrap">
				<table className="rep">
					<thead>
						<tr>
							<th className="rep__lbl">{tkr ? 'Belopp i tkr' : 'Belopp i kronor'}</th>
							{spans.map(sp => <th key={sp.label}>{sp.label}</th>)}
							<th>Ack</th>
						</tr>
					</thead>
					<tbody>
						{data.groups.map(gr => (
							<Group key={gr.key} g={{
								...gr, subtotal: flow(gr.subtotal),
								accounts: gr.accounts.map(a => ({...a, values: flow(a.values)})),
							}} open={open.has(gr.key)} onToggle={() => toggle(gr.key)} fmt={fmt} exact={exact}
							drill={drill} onDrill={openDrill} shadow={shadow} />
						))}

						<Total label="SUMMA RÖRELSENS INTÄKTER" values={flow(data.income)} fmt={fmt} exact={exact} />
						<Total label="SUMMA RÖRELSENS KOSTNADER" values={flow(data.costs)} fmt={fmt} exact={exact} />
						<Total label="BERÄKNAT RESULTAT" values={flow(data.result)} fmt={fmt} exact={exact} strong />
						{/* Already a running figure: it takes the period's CLOSING value, never
						    the sum of its months, or a quarter would read three times its result. */}
						<Total label="Ackumulerat resultat" values={stock(data.result_accumulated)} fmt={fmt} exact={exact}
							total={data.totals.result} />

						{/* The reason this page exists: earned, then held, on adjacent lines. */}
						<tr className="rep__band"><td colSpan={spans.length + 2}>Likviditet</td></tr>
						{/* Cash is a STOCK — the period's closing balance, never its months added. */}
						<Total label="KASSA (utgående saldo)" values={stock(data.cash)} fmt={fmt} exact={exact} strong noTotal />
						<Total label="Kassaförändring" values={flow(data.cash_movement)} fmt={fmt} exact={exact} />

						<tr className="rep__band"><td colSpan={spans.length + 2}>
							Balanskonton — {mode === 'balance' ? 'utgående saldo' : 'förändring'} per {g.label.toLowerCase()}
						</td></tr>
						{data.balance_groups.filter(gr => gr.accounts.length).map(gr => {
							const isBal = mode === 'balance';
							const r = v => (isBal ? stock(v) : flow(v));
							return (
								<Group key={gr.key} g={{
									...gr,
									subtotal: r(isBal ? gr.subtotal : gr.subtotal_movement),
									accounts: gr.accounts.map(a => ({
										...a, values: r(isBal ? a.values : a.movement),
									})),
								}} open={open.has(gr.key)} onToggle={() => toggle(gr.key)} fmt={fmt} exact={exact}
								noTotal={isBal} drill={drill} onDrill={openDrill} shadow={shadow} />
							);
						})}
					</tbody>
				</table>
			</div>

			<p className="foot-note">
				Intäkter positiva och kostnader negativa, som i en resultatrapport — huvudboken
				lagrar kredit negativt och tecknet vänds en gång, här. Balanskonton behåller
				huvudbokens eget tecken: tillgångar positiva, skulder negativa. Utgående saldo
				är framvandrat från första verifikatet, inte årets rörelse, så ett konto utan
				verifikat en månad bär föregående saldo vidare i stället för att läsas som noll.
				Små avvikelser mot Fortnox utskrift beror på att Fortnox avrundar varje rad till
				tusental innan de summeras; här summeras kronor och avrundas en gång.
			</p>
		</>
	);
}

/**
 * A section: subtotal always, accounts on demand. Sixty accounts across twelve columns is
 * not a report, it is a data dump — the shape of the year lives in the subtotals, and the
 * account detail is what you open once you know which row to ask about.
 */
const Group = memo(function Group({g, open, onToggle, fmt, exact, noTotal, drill, onDrill, shadow}) {
	const total = g.subtotal.reduce((a, b) => a + b, 0);
	const cols = g.subtotal.length + 2;
	return (
		<>
			<tr className="rep__grp" onClick={onToggle}>
				<td className="rep__lbl">
					<span className={`caret${open ? ' caret--open' : ''}`} />{g.label}
					<span className="stamp"> · {g.accounts.length} konton</span>
				</td>
				{g.subtotal.map((v, i) => <td key={i} title={exact(v)}>{fmt(v)}</td>)}
				<td className="num--strong" title={exact(total)}>{noTotal ? '—' : fmt(total)}</td>
			</tr>
			{open && g.accounts.map(a => {
				const isOpen = drill?.account === a.account;
				return (
					<Fragment key={a.account}>
						<tr className={`rep__acc${isOpen ? ' rep__acc--open' : ''}`}>
							{/* The label drills the whole year; a cell drills its own column. Both
							    are wanted: "what is in this account" and "what made THAT number". */}
							<td className="rep__lbl rep__acc__lbl" onClick={() => onDrill(a.account, null)}>
								<span className={`caret${isOpen ? ' caret--open' : ''}`} />
								<span className="rep__no">{a.account}</span> {a.name}
							</td>
							{a.values.map((v, i) => (
								<td key={i} className="rep__cell" title={exact(v)}
									onClick={() => onDrill(a.account, i)}>{fmt(v)}</td>
							))}
							<td>{noTotal ? '—' : fmt(a.values.reduce((x, y) => x + y, 0))}</td>
						</tr>
						{isOpen && (
							<tr className="rep__drill">
								<td colSpan={cols}>
									<AccountDrill account={a.account} name={a.name}
										period={drill.period} shadow={shadow} />
								</td>
							</tr>
						)}
					</Fragment>
				);
			})}
		</>
	);
});

function Total({label, values, fmt, exact, strong, total, noTotal}) {
	return (
		<tr className={`rep__sum${strong ? ' rep__sum--strong' : ''}`}>
			<td className="rep__lbl">{label}</td>
			{values.map((v, i) => <td key={i} title={exact(v)}>{fmt(v)}</td>)}
			<td className="num--strong">
				{noTotal ? '—' : fmt(total ?? values.reduce((a, b) => a + b, 0))}
			</td>
		</tr>
	);
}
